import { useEffect, useRef, useCallback, useState } from 'react';
import type { GamePhase } from '../types/bountifulSea';

// Import audio files
import songGame from '../components/BountifulSea/song_game.mp3';
import songGameEnd from '../components/BountifulSea/song_game_end.mp3';
import seaAmbience1 from '../components/BountifulSea/sea_ambience_01.mp3';
import seaAmbience2 from '../components/BountifulSea/sea_ambience_02.mp3';
import seaAmbience3 from '../components/BountifulSea/sea_ambience_03.mp3';
import hullCreaking from '../components/BountifulSea/hull_creaking.mp3';

// ═══════════════════════════════════════════════════════════════
// BOUNTIFUL SEA - AUDIO MANAGEMENT HOOK
// ═══════════════════════════════════════════════════════════════

// Relative volume levels (these will be multiplied by master volume)
const RELATIVE_VOLUMES = {
  gameMusic: 0.8,       // Main game song - loudest
  seaAmbience: 0.5,     // Ocean sounds - medium
  hullCreaking: 0.35,   // Ship creaking - subtle
  victoryMusic: 1.0,    // End game music - full relative volume
};

// Default master volume (10%)
const DEFAULT_MASTER_VOLUME = 0.10;

// Randomly select a sea ambience track
const seaAmbienceTracks = [seaAmbience1, seaAmbience2, seaAmbience3];

export function useBountifulSeaAudio(phase: GamePhase) {
  // Master volume state (default 10%)
  const [masterVolume, setMasterVolume] = useState(DEFAULT_MASTER_VOLUME);
  const [isMuted, setIsMuted] = useState(false);

  // Audio element refs
  const gameMusicRef = useRef<HTMLAudioElement | null>(null);
  const seaAmbienceRef = useRef<HTMLAudioElement | null>(null);
  const hullCreakingRef = useRef<HTMLAudioElement | null>(null);
  const victoryMusicRef = useRef<HTMLAudioElement | null>(null);

  // Calculate actual volume for a track
  const getVolume = useCallback((relativeVolume: number) => {
    if (isMuted) return 0;
    return masterVolume * relativeVolume;
  }, [masterVolume, isMuted]);

  // Update all audio element volumes
  const updateVolumes = useCallback(() => {
    if (gameMusicRef.current) {
      gameMusicRef.current.volume = getVolume(RELATIVE_VOLUMES.gameMusic);
    }
    if (seaAmbienceRef.current) {
      seaAmbienceRef.current.volume = getVolume(RELATIVE_VOLUMES.seaAmbience);
    }
    if (hullCreakingRef.current) {
      hullCreakingRef.current.volume = getVolume(RELATIVE_VOLUMES.hullCreaking);
    }
    if (victoryMusicRef.current) {
      victoryMusicRef.current.volume = getVolume(RELATIVE_VOLUMES.victoryMusic);
    }
  }, [getVolume]);

  // Initialize audio elements
  const initializeAudio = useCallback(() => {
    // Game music
    if (!gameMusicRef.current) {
      gameMusicRef.current = new Audio(songGame);
      gameMusicRef.current.loop = true;
      gameMusicRef.current.volume = getVolume(RELATIVE_VOLUMES.gameMusic);
    }

    // Sea ambience (random selection)
    if (!seaAmbienceRef.current) {
      const randomIndex = Math.floor(Math.random() * seaAmbienceTracks.length);
      const selectedAmbience = seaAmbienceTracks[randomIndex];
      seaAmbienceRef.current = new Audio(selectedAmbience);
      seaAmbienceRef.current.loop = true;
      seaAmbienceRef.current.volume = getVolume(RELATIVE_VOLUMES.seaAmbience);
    }

    // Hull creaking
    if (!hullCreakingRef.current) {
      hullCreakingRef.current = new Audio(hullCreaking);
      hullCreakingRef.current.loop = true;
      hullCreakingRef.current.volume = getVolume(RELATIVE_VOLUMES.hullCreaking);
    }

    // Victory music
    if (!victoryMusicRef.current) {
      victoryMusicRef.current = new Audio(songGameEnd);
      victoryMusicRef.current.loop = true;
      victoryMusicRef.current.volume = getVolume(RELATIVE_VOLUMES.victoryMusic);
    }
  }, [getVolume]);

  // Update volumes when master volume or mute state changes
  useEffect(() => {
    updateVolumes();
  }, [updateVolumes]);

  // Start game audio (music + ambience + creaking)
  const startGameAudio = useCallback(() => {
    initializeAudio();

    const playPromises: Promise<void>[] = [];

    if (gameMusicRef.current) {
      gameMusicRef.current.currentTime = 0;
      playPromises.push(gameMusicRef.current.play().catch(() => {}));
    }
    if (seaAmbienceRef.current) {
      seaAmbienceRef.current.currentTime = 0;
      playPromises.push(seaAmbienceRef.current.play().catch(() => {}));
    }
    if (hullCreakingRef.current) {
      hullCreakingRef.current.currentTime = 0;
      playPromises.push(hullCreakingRef.current.play().catch(() => {}));
    }

    return Promise.all(playPromises);
  }, [initializeAudio]);

  // Stop game audio
  const stopGameAudio = useCallback(() => {
    if (gameMusicRef.current) {
      gameMusicRef.current.pause();
      gameMusicRef.current.currentTime = 0;
    }
    if (seaAmbienceRef.current) {
      seaAmbienceRef.current.pause();
      seaAmbienceRef.current.currentTime = 0;
    }
    if (hullCreakingRef.current) {
      hullCreakingRef.current.pause();
      hullCreakingRef.current.currentTime = 0;
    }
  }, []);

  // Start victory audio
  const startVictoryAudio = useCallback(() => {
    initializeAudio();
    stopGameAudio();

    if (victoryMusicRef.current) {
      victoryMusicRef.current.currentTime = 0;
      victoryMusicRef.current.play().catch(() => {});
    }
  }, [initializeAudio, stopGameAudio]);

  // Stop victory audio
  const stopVictoryAudio = useCallback(() => {
    if (victoryMusicRef.current) {
      victoryMusicRef.current.pause();
      victoryMusicRef.current.currentTime = 0;
    }
  }, []);

  // Stop all audio
  const stopAllAudio = useCallback(() => {
    stopGameAudio();
    stopVictoryAudio();
  }, [stopGameAudio, stopVictoryAudio]);

  // Toggle mute
  const toggleMute = useCallback(() => {
    setIsMuted(prev => !prev);
  }, []);

  // Handle phase changes
  useEffect(() => {
    // Setup phase - no audio
    if (phase === 'setup') {
      stopAllAudio();
      return;
    }

    // Victory phase - play victory music
    if (phase === 'victory') {
      startVictoryAudio();
      return;
    }

    // All other phases - play game audio
    // Only start if not already playing
    if (gameMusicRef.current?.paused !== false) {
      startGameAudio();
    }
  }, [phase, startGameAudio, startVictoryAudio, stopAllAudio]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopAllAudio();
      // Clean up audio elements
      gameMusicRef.current = null;
      seaAmbienceRef.current = null;
      hullCreakingRef.current = null;
      victoryMusicRef.current = null;
    };
  }, [stopAllAudio]);

  return {
    // Audio controls
    masterVolume,
    setMasterVolume,
    isMuted,
    toggleMute,
    // Manual controls (mostly for internal use)
    startGameAudio,
    stopGameAudio,
    startVictoryAudio,
    stopVictoryAudio,
    stopAllAudio,
  };
}

export default useBountifulSeaAudio;
